import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

import PaiButton from 'features/ui/base-components/pai-button';

interface Props {
  open: boolean;
  loading: boolean;
  workUnit: {
    id: string;
    workType: string;
    status: 'pending' | 'processing' | 'failed' | 'finished';
    funnel?: {
      name: string;
    };
  } | null;
  handleClose: () => void;
  handleConfirm: () => void;
}

const DeleteWorkUnitDialog: React.FC<Props> = ({
  open,
  loading,
  workUnit,
  handleClose,
  handleConfirm
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const getActionText = () => {
    if (!workUnit) return t('delete');

    switch (workUnit.status) {
      case 'processing':
        return t('interrupt');
      case 'pending':
        return t('delete');
      case 'finished':
      case 'failed':
        return t('deleteFromHistory');
      default:
        return t('delete');
    }
  };

  const getConfirmationMessage = () => {
    if (!workUnit) return '';

    const campaignName = workUnit.funnel?.name || t('unknownCampaign');
    const workType = t(`workUnit-${workUnit.workType}`, { defaultValue: workUnit.workType });

    switch (workUnit.status) {
      case 'processing':
        return t('confirmInterruptWorkUnit', {
          workType,
          campaignName,
          defaultValue: `Are you sure you want to interrupt this ${workType} task for ${campaignName}? This will stop the current processing.`
        });
      case 'pending':
        return t('confirmDeletePendingWorkUnit', {
          workType,
          campaignName,
          defaultValue: `Are you sure you want to delete this pending ${workType} task for ${campaignName}?`
        });
      case 'finished':
      case 'failed':
        return t('confirmDeleteFinishedWorkUnit', {
          workType,
          campaignName,
          defaultValue: `Are you sure you want to remove this ${workType} task for ${campaignName} from history?`
        });
      default:
        return t('confirmDeleteWorkUnit', {
          workType,
          campaignName,
          defaultValue: `Are you sure you want to delete this ${workType} task for ${campaignName}?`
        });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="confirm-delete-work-unit-dialog-title"
      classes={{ paper: classes.dialogPaper }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirm-delete-work-unit-dialog-title" className={classes.dialogTitle}>
        {t('confirmDeletion')}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography variant="body1">
          {getConfirmationMessage()}
        </Typography>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <PaiButton
          text={t('cancel')}
          action={handleClose}
          disabled={loading}
          size="small"
          border="rounded"
        />
        <PaiButton
          text={getActionText()}
          action={handleConfirm}
          loading={loading}
          size="small"
          border="rounded"
          color="error"
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    borderRadius: '12px',
    padding: theme.spacing(2),
  },
  dialogTitle: {
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.text.primary,
  },
  dialogContent: {
    paddingBottom: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
}));

export default DeleteWorkUnitDialog;

